import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { siteService, userService } from '../../services';
import { Site, User } from '../../types';
import { DEFAULT_PAGE_SIZE, USER_ROLES } from '../../constants';
import { usePagination } from '../../hooks';
import { OpeningHoursEditor } from '../../components/OpeningHoursEditor';
import { toast } from '../../components/Toast';
import styles from './Sites.module.css';

export const Sites: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [managers, setManagers] = useState<User[]>([]);

  // Use pagination custom hook
  const { page, totalPages, setTotalPages, resetPage, nextPage, previousPage, isFirstPage, isLastPage, hasMultiplePages } = usePagination();

  // Filtri
  const [activeFilter, setActiveFilter] = useState<boolean | ''>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    phoneNumber: '',
    managerId: '' as number | '',
    openingHours: '',
    companyId: currentUser?.companyId || 0,
  });

  // Carica lista manager disponibili
  const loadManagers = useCallback(async () => {
    try {
      const response = await userService.getUsers({
        role: USER_ROLES.MANAGER,
        companyId: currentUser?.companyId,
        pageSize: 100,
      });
      setManagers(response.data);
    } catch (err: unknown) {
      console.error('Errore nel caricamento dei manager:', err);
    }
  }, [currentUser?.companyId]);

  // Memoize loadSites to prevent recreation on every render
  const loadSites = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const filter: {
        page: number;
        pageSize: number;
        isActive?: boolean;
        search?: string;
        companyId?: number;
      } = { page, pageSize: DEFAULT_PAGE_SIZE.SITES || 20 };

      if (activeFilter !== '') {
        filter.isActive = activeFilter;
      }

      if (searchTerm) {
        filter.search = searchTerm;
      }

      if (currentUser?.companyId) {
        filter.companyId = currentUser.companyId;
      }

      const response = await siteService.getSites(filter);
      setSites(response.data);
      setTotalPages(response.totalPages);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel caricamento delle sedi';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [page, activeFilter, searchTerm, currentUser?.companyId, setTotalPages]);

  useEffect(() => {
    loadSites();
    loadManagers();
  }, [loadSites, loadManagers]);

  // Memoize initial form data
  const initialFormData = useMemo(
    () => ({
      name: '',
      address: '',
      city: '',
      postalCode: '',
      phoneNumber: '',
      managerId: '' as number | '',
      openingHours: '',
      companyId: currentUser?.companyId || 0,
    }),
    [currentUser?.companyId]
  );

  // Memoize handleSearch
  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      resetPage();
      loadSites();
    },
    [resetPage, loadSites]
  );

  // Memoize openCreateModal
  const openCreateModal = useCallback(() => {
    setEditingSite(null);
    setFormData(initialFormData);
    setShowModal(true);
  }, [initialFormData]);

  // Memoize openEditModal
  const openEditModal = useCallback((site: Site) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      address: site.address,
      city: site.city,
      postalCode: site.postalCode || '',
      phoneNumber: site.phoneNumber || '',
      managerId: site.managerId || '',
      openingHours: site.openingHours || '',
      companyId: site.companyId,
    });
    setShowModal(true);
  }, []);

  // Memoize closeModal
  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingSite(null);
    setFormData(initialFormData);
    setError('');
  }, [initialFormData]);

  // Memoize handleSubmit
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');

      try {
        if (editingSite) {
          // Update
          await siteService.updateSite(editingSite.id, {
            name: formData.name,
            address: formData.address,
            city: formData.city,
            postalCode: formData.postalCode || undefined,
            phoneNumber: formData.phoneNumber || undefined,
            managerId: formData.managerId || undefined,
            openingHours: formData.openingHours || undefined,
          });
          toast.success('Sede aggiornata con successo!');
        } else {
          // Create
          await siteService.createSite({
            companyId: formData.companyId,
            name: formData.name,
            address: formData.address,
            city: formData.city,
            postalCode: formData.postalCode || undefined,
            phoneNumber: formData.phoneNumber || undefined,
            managerId: formData.managerId || undefined,
            openingHours: formData.openingHours || undefined,
          });
          toast.success('Sede creata con successo!');
        }
        closeModal();
        loadSites();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Errore nel salvataggio della sede';
        setError(message);
      }
    },
    [editingSite, formData, closeModal, loadSites]
  );

  // Memoize handleDeactivate
  const handleDeactivate = useCallback(
    async (id: number, name: string) => {
      if (!window.confirm(`Sei sicuro di voler disattivare la sede "${name}"?\n\nNOTA: Non puoi disattivare una sede con dipendenti attivi o turni futuri.`)) {
        return;
      }

      try {
        await siteService.deactivateSite(id);
        toast.success('Sede disattivata con successo');
        loadSites();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Errore nella disattivazione della sede';
        toast.error(message);
      }
    },
    [loadSites]
  );

  // Memoize filter change handlers
  const handleActiveFilterChange = useCallback(
    (value: string) => {
      setActiveFilter(value === '' ? '' : value === 'true');
      resetPage();
    },
    [resetPage]
  );

  return (
    <div className={styles.sites}>
      <div className={styles.header}>
        <h1>🏢 Gestione Sedi</h1>
        {currentUser && currentUser.role <= USER_ROLES.OWNER && (
          <button className={styles.createButton} onClick={openCreateModal}>
            ➕ Nuova Sede
          </button>
        )}
      </div>

      {/* Filtri e ricerca */}
      <div className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Cerca sede per nome, città o indirizzo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.searchButton}>
            🔍 Cerca
          </button>
        </form>

        <div className={styles.filterGroup}>
          <label htmlFor="active">Stato:</label>
          <select
            id="active"
            value={activeFilter === '' ? '' : activeFilter ? 'true' : 'false'}
            onChange={(e) => handleActiveFilterChange(e.target.value)}
            className={styles.select}
          >
            <option value="">Tutte</option>
            <option value="true">Attive</option>
            <option value="false">Disattivate</option>
          </select>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>Caricamento sedi...</div>
      ) : sites.length === 0 ? (
        <div className={styles.empty}>
          <p>📭 Nessuna sede trovata</p>
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Nome Sede</th>
                  <th>Indirizzo</th>
                  <th>Città</th>
                  <th>Telefono</th>
                  <th>Manager</th>
                  <th>Dipendenti</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => (
                  <tr key={site.id}>
                    <td className={styles.siteName}>{site.name}</td>
                    <td>{site.address}</td>
                    <td>
                      {site.city}
                      {site.postalCode && ` (${site.postalCode})`}
                    </td>
                    <td>{site.phoneNumber || '-'}</td>
                    <td>{site.managerName || '-'}</td>
                    <td className={styles.center}>{site.employeeCount}</td>
                    <td>
                      <span className={site.isActive ? styles.badgeActive : styles.badgeInactive}>
                        {site.isActive ? '✓ Attiva' : '✗ Disattivata'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.editButton}
                          onClick={() => openEditModal(site)}
                          title="Modifica"
                        >
                          ✏️
                        </button>
                        {site.isActive && (
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleDeactivate(site.id, site.name)}
                            title="Disattiva"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMultiplePages && (
            <div className={styles.pagination}>
              <button
                onClick={previousPage}
                disabled={isFirstPage}
                className={styles.pageButton}
              >
                ← Precedente
              </button>
              <span className={styles.pageInfo}>
                Pagina {page} di {totalPages}
              </span>
              <button
                onClick={nextPage}
                disabled={isLastPage}
                className={styles.pageButton}
              >
                Successiva →
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal per crea/modifica */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingSite ? 'Modifica Sede' : 'Nuova Sede'}</h2>
              <button className={styles.closeButton} onClick={closeModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="name">Nome Sede *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="address">Indirizzo *</label>
                <input
                  type="text"
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="city">Città *</label>
                  <input
                    type="text"
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="postalCode">CAP</label>
                  <input
                    type="text"
                    id="postalCode"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="phoneNumber">Telefono</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="managerId">Manager</label>
                <select
                  id="managerId"
                  value={formData.managerId}
                  onChange={(e) => setFormData({ ...formData, managerId: e.target.value ? parseInt(e.target.value) : '' })}
                  className={styles.select}
                >
                  <option value="">-- Nessun Manager --</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.fullName} ({manager.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <OpeningHoursEditor
                  value={formData.openingHours}
                  onChange={(value) => setFormData({ ...formData, openingHours: value })}
                />
              </div>

              {error && <div className={styles.error}>❌ {error}</div>}

              <button type="submit" className={styles.submitButton}>
                {editingSite ? '💾 Salva Modifiche' : '➕ Crea Sede'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

Sites.displayName = 'Sites';
